import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  Collapse,
  Divider,
  Chip,
  Grid,
  useMediaQuery,
  useTheme,
  alpha,
} from "@mui/material";
import Popover from "@mui/material/Popover";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  getAllFeedbacks,
  createFeedback,
  updateFeedback,
  deleteFeedback,
} from "../../../Services/Services";

const ratingOptions = [
  { value: 1, label: "Very Poor" },
  { value: 2, label: "Poor" },
  { value: 3, label: "Average" },
  { value: 4, label: "Good" },
  { value: 5, label: "Excellent" },
];

const RoleManagementTable = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // ── Theme-aware helpers ──
  // ✅✅✅ FIX #1: TableHead bg dark mode me SOLID kar diya
  // Pehle: alpha(theme.palette.primary.main, 0.15) → TRANSLUCENT tha
  // Sticky header (S. No. / Action) ke PEECHE se scroll ho raha content dikhta tha
  // Niyam yaad rakho: STICKY ho to SOLID color — alpha() allowed nahi
  const tableHeadBg = isDark ? "#252b38" : "#f5f5f5";

  // ✅✅✅ FIX #2: Zebra rows bhi dark me SOLID kar diye
  // Pehle: alpha(theme.palette.common.white, 0.035) → translucent
  // (sticky S.No / Action cells `bgcolor: inherit` use karte hain, to row ka color
  // solid hona hi chahiye warna peeche ka content padta hai)
  // Light mode BILKUL SAME rakha hai (#fafafa + paper)
  const rowBg = (index) =>
    isDark
      ? index % 2 === 0
        ? "#20242e"
        : "#1a1d26"
      : index % 2 === 0
      ? "#fafafa"
      : theme.palette.background.paper;

  // ── Dialog shared Paper styles ──
  const dialogPaperSx = {
    bgcolor: "background.paper",
    backgroundImage: "none",
    border: "1px solid",
    borderColor: "divider",
    boxShadow: isDark
      ? "0 12px 40px rgba(0,0,0,0.65)"
      : "0 8px 32px rgba(0,0,0,0.14)",
  };

  const getSelectedDivision = () => {
    try {
      const stored = localStorage.getItem("selectedDivision");
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      console.error("Failed to parse selectedDivision:", e);
      return null;
    }
  };
  const storedDivision = getSelectedDivision();

  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");
  const [sortAsc, setSortAsc] = useState(true);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [expandedFeedback, setExpandedFeedback] = useState(null);

  const [formData, setFormData] = useState({
    client_name: "",
    client_email: "",
    company_name: "",
    feedback: "",
    rating: 3,
    is_resolved: false,
    division: storedDivision?.division_id || "",
  });
  const [editId, setEditId] = useState(null);

  useEffect(() => {
    fetchFeedbacks();
  }, []);

  useEffect(() => {
    document.body.style.overflow = isMobile ? "auto" : "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isMobile]);

  const fetchFeedbacks = async () => {
    try {
      const data = await getAllFeedbacks();
      setRows(data);
    } catch (error) {
      console.error("Error fetching feedbacks:", error);
    }
  };

  const handleDelete = async (id) => {
    try {
      const success = await deleteFeedback(id);
      if (success) {
        setRows((prev) => prev.filter((r) => r.feedback_id !== id));
        toast.success("Feedback deleted successfully!");
      }
    } catch (error) {
      console.error("Delete failed:", error);
      toast.error("Failed to delete feedback!");
    }
  };

  const handleAddNewRow = async () => {
    try {
      const user = JSON.parse(localStorage.getItem("user_id"));
      const formDataWithUser = { ...formData, created_by: user };
      const data = await createFeedback(formDataWithUser);
      setRows([...rows, data]);
      setFormData({
        client_name: "",
        client_email: "",
        company_name: "",
        feedback: "",
        rating: 3,
        is_resolved: false,
        division: storedDivision?.division_id || "",
      });
      setOpenAddModal(false);
      toast.success("Feedback submitted successfully!");
    } catch (error) {
      console.error("Add failed:", error);
      toast.error("Failed to submit feedback!");
    }
  };

  const handleEdit = (id) => {
    const record = rows.find((r) => r.feedback_id === id);
    if (record) {
      setFormData({ ...record });
      setEditId(id);
      setOpenEditModal(true);
    }
  };

  const handleUpdateRow = async () => {
    try {
      const updated = await updateFeedback(editId, formData);
      setRows((prev) =>
        prev.map((row) => (row.feedback_id === editId ? updated : row))
      );
      setOpenEditModal(false);
      setEditId(null);
      toast.success("Feedback updated successfully!");
    } catch (error) {
      console.error("Update failed:", error);
      toast.error("Failed to update feedback!");
    }
  };

  const getFilteredSortedRows = () => {
    let filtered = rows.filter((row) =>
      row.client_name.toLowerCase().includes(filter.toLowerCase())
    );
    filtered.sort((a, b) =>
      sortAsc
        ? a.client_name.localeCompare(b.client_name)
        : b.client_name.localeCompare(a.client_name)
    );
    return filtered;
  };

  const getRatingColor = (rating) => {
    switch (rating) {
      case 5: return "#4caf50";
      case 4: return "#8bc34a";
      case 3: return "#ff9800";
      case 2: return "#ff5722";
      case 1: return "#f44336";
      default: return "#757575";
    }
  };

  const filteredRows = getFilteredSortedRows();

  return (
    <Box
      sx={{
        px: 0,
        pt: 0,
        mt: { xs: 3, md: -3 },
        maxWidth: 1200,
        mx: "auto",
        bgcolor: "background.default",
        minHeight: "100%",
      }}
    >
      <ToastContainer
        position="top-right"
        autoClose={3000}
        theme={isDark ? "dark" : "light"}
      />

      {/* ── FILTERS BAR ── */}
      <Box
        sx={{
          mb: 2,
          position: "sticky",
          top: 0,
          zIndex: 1100,
          pt: 1,
          pb: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: (t) => alpha(t.palette.background.paper, 0.95),
          backdropFilter: "blur(8px)",
          boxShadow: isDark
            ? "0 2px 10px rgba(0,0,0,0.45)"
            : "0 1px 4px rgba(0,0,0,0.07)",
        }}
      >
        <Grid container spacing={1} alignItems="center">
          {/* Search */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Search by client name"
              size="small"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              sx={{
                "& .MuiInputBase-root": { height: 32, borderRadius: 0 },
                "& .MuiInputLabel-root": { fontSize: "0.7rem" },
              }}
            />
          </Grid>

          {/* Total count */}
          <Grid item xs={6} sm={2}>
            <Typography
              sx={{
                border: "1px solid",
                borderColor: "divider",
                px: 2,
                py: 0.5,
                borderRadius: 0,
                fontSize: "0.7rem",
                height: 32,
                display: "flex",
                alignItems: "center",
                bgcolor: (t) =>
                  t.palette.mode === "dark"
                    ? alpha(t.palette.common.white, 0.06)
                    : "#f5f5f5",
                color: "text.primary",
              }}
            >
              Total: {filteredRows.length}
            </Typography>
          </Grid>

          {/* Sort */}
          <Grid item xs={6} sm={2}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => setSortAsc(!sortAsc)}
              sx={{
                height: 32,
                fontSize: "0.65rem",
                borderRadius: 0,
                whiteSpace: "nowrap",
              }}
            >
              Sort ({sortAsc ? "A-Z" : "Z-A"})
            </Button>
          </Grid>

          {/* Add */}
          <Grid item xs={12} sm={2}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={() => setOpenAddModal(true)}
              startIcon={<AddIcon />}
              sx={{ height: 32, fontSize: "0.7rem", borderRadius: 0 }}
            >
              Feedback
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* ── MOBILE VIEW (CARDS) ── */}
      {isMobile ? (
        <Box sx={{ bgcolor: "background.paper" }}>
          {filteredRows.map((row, index) => (
            <Box key={row.feedback_id}>
              {/* Main Row */}
              <Box
                onClick={() =>
                  setExpandedFeedback(
                    expandedFeedback === row.feedback_id
                      ? null
                      : row.feedback_id
                  )
                }
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: 1.5,
                  cursor: "pointer",
                  bgcolor: rowBg(index),
                  "&:active": { bgcolor: "action.selected" },
                  borderLeft: `4px solid ${getRatingColor(row.rating)}`,
                  transition: "background-color 0.15s ease",
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 0.5,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        color: "text.disabled",
                        minWidth: 25,
                      }}
                    >
                      #{index + 1}
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: "bold",
                        fontSize: "0.85rem",
                        flex: 1,
                        color: "text.primary",
                      }}
                    >
                      {row.client_name}
                    </Typography>
                    <Chip
                      label={
                        ratingOptions.find((opt) => opt.value === row.rating)
                          ?.label
                      }
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: "0.65rem",
                        bgcolor: getRatingColor(row.rating),
                        color: "#fff",
                        "& .MuiChip-label": { px: 1 },
                      }}
                    />
                  </Box>
                </Box>

                <IconButton size="small">
                  {expandedFeedback === row.feedback_id ? (
                    <ExpandLess fontSize="small" />
                  ) : (
                    <ExpandMore fontSize="small" />
                  )}
                </IconButton>
              </Box>

              {/* Expanded Details */}
              <Collapse
                in={expandedFeedback === row.feedback_id}
                timeout="auto"
                unmountOnExit
              >
                <Box
                  sx={{
                    px: 1.5,
                    py: 1,
                    bgcolor: (t) =>
                      t.palette.mode === "dark"
                        ? alpha(t.palette.common.white, 0.05)
                        : alpha(t.palette.primary.main, 0.04),
                    borderLeft: `4px solid ${getRatingColor(row.rating)}`,
                  }}
                >
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr",
                      gap: 0.8,
                      mb: 1,
                    }}
                  >
                    {/* Email */}
                    <Box>
                      <Typography
                        sx={{
                          fontSize: "0.6rem",
                          color: "text.disabled",
                          textTransform: "uppercase",
                          letterSpacing: "0.08em",
                          fontWeight: 600,
                        }}
                      >
                        EMAIL
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.72rem",
                          fontWeight: 500,
                          color: "text.secondary",
                          wordBreak: "break-all",
                        }}
                      >
                        {row.client_email}
                      </Typography>
                    </Box>

                    {/* Company */}
                    <Box>
                      <Typography
                        sx={{
                          fontSize: "0.6rem",
                          color: "text.disabled",
                          textTransform: "uppercase",
                          letterSpacing: "0.08em",
                          fontWeight: 600,
                        }}
                      >
                        COMPANY
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.72rem",
                          fontWeight: 500,
                          color: "text.secondary",
                        }}
                      >
                        {row.company_name}
                      </Typography>
                    </Box>

                    {/* Feedback */}
                    <Box>
                      <Typography
                        sx={{
                          fontSize: "0.6rem",
                          color: "text.disabled",
                          textTransform: "uppercase",
                          letterSpacing: "0.08em",
                          fontWeight: 600,
                        }}
                      >
                        FEEDBACK
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.72rem",
                          color: "text.secondary",
                          lineHeight: 1.4,
                        }}
                      >
                        {row.feedback}
                      </Typography>
                    </Box>

                    {/* Resolved */}
                    <Box>
                      <Typography
                        sx={{
                          fontSize: "0.6rem",
                          color: "text.disabled",
                          textTransform: "uppercase",
                          letterSpacing: "0.08em",
                          fontWeight: 600,
                        }}
                      >
                        RESOLVED
                      </Typography>
                      <Chip
                        label={row.is_resolved ? "Yes" : "No"}
                        size="small"
                        color={row.is_resolved ? "success" : "default"}
                        sx={{ height: 20, fontSize: "0.65rem", mt: 0.3 }}
                      />
                    </Box>
                  </Box>

                  <Divider sx={{ my: 1, borderColor: "divider" }} />

                  {/* Actions */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 0.6,
                    }}
                  >
                    {/* Edit */}
                    <IconButton
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleEdit(row.feedback_id);
                      }}
                      sx={{
                        bgcolor: (t) =>
                          t.palette.mode === "dark"
                            ? alpha(t.palette.primary.main, 0.15)
                            : "#e3f2fd",
                        borderRadius: "6px",
                        width: 32,
                        height: 32,
                        "&:hover": {
                          bgcolor: (t) =>
                            t.palette.mode === "dark"
                              ? alpha(t.palette.primary.main, 0.28)
                              : "#bbdefb",
                        },
                      }}
                    >
                      <EditIcon
                        sx={{ fontSize: "1rem", color: "primary.main" }}
                      />
                    </IconButton>

                    {/* Delete */}
                    <IconButton
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        setAnchorEl(event.currentTarget);
                        setProjectToDelete(row.feedback_id);
                      }}
                      sx={{
                        bgcolor: (t) =>
                          t.palette.mode === "dark"
                            ? alpha(t.palette.error.main, 0.15)
                            : "#ffebee",
                        borderRadius: "6px",
                        width: 32,
                        height: 32,
                        "&:hover": {
                          bgcolor: (t) =>
                            t.palette.mode === "dark"
                              ? alpha(t.palette.error.main, 0.28)
                              : "#ffcdd2",
                        },
                      }}
                    >
                      <DeleteIcon
                        sx={{ fontSize: "1rem", color: "error.main" }}
                      />
                    </IconButton>
                  </Box>
                </Box>
              </Collapse>

              <Divider />
            </Box>
          ))}
        </Box>
      ) : (
        /* ── DESKTOP TABLE VIEW ── */
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            maxHeight: "88vh",
            overflow: "auto",
            bgcolor: "background.paper",
            backgroundImage: "none",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: isDark ? "0 4px 18px rgba(0,0,0,0.35)" : "none",
          }}
        >
          <Table
            stickyHeader
            size="small"
            sx={{
              minWidth: 1600,
              "& td, & th": {
                textAlign: "center",
                whiteSpace: "nowrap",
                borderBottom: "1px solid",
                borderColor: "divider",
              },
            }}
          >
            <TableHead
              sx={{
                "& th": {
                  fontWeight: "bold",
                  fontSize: "0.8rem",
                  color: "text.primary",
                  // ✅ KEY FIX: theme-aware sticky header (ab FIX #1 ke baad tableHeadBg SOLID hai)
                  backgroundColor: `${tableHeadBg} !important`,
                  borderBottom: "2px solid",
                  borderColor: "primary.main",
                  letterSpacing: "0.04em",
                },
              }}
            >
              <TableRow>
                <TableCell
                  sx={{
                    position: "sticky",
                    left: 0,
                    // ✅ FIX #1 ki wajah se ye SOLID hai ab (pehle alpha 0.15 = translucent)
                    backgroundColor: `${tableHeadBg} !important`,
                    zIndex: 3,
                  }}
                >
                  S. No.
                </TableCell>
                <TableCell>Client Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Company</TableCell>
                <TableCell>Feedback</TableCell>
                <TableCell>Rating</TableCell>
                <TableCell>Resolved</TableCell>
                <TableCell
                  sx={{
                    position: "sticky",
                    right: 0,
                    // ✅ FIX #1 ki wajah se ye SOLID hai ab (pehle alpha 0.15 = translucent)
                    backgroundColor: `${tableHeadBg} !important`,
                    zIndex: 3,
                  }}
                >
                  Action
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredRows.map((row, index) => (
                <TableRow
                  // ✅✅✅ FIX #3: `hover` prop HATA DIYA — MUI ka default hover
                  // translucent (action.hover) hota hai, jo dark mode me sticky
                  // cells ke peeche se DIKHTA tha. Neeche apna solid hover hai.
                  key={index}
                  sx={{
                    backgroundColor: rowBg(index),
                    transition: "background-color 0.15s ease",
                    "&:hover": {
                      // ✅✅✅ FIX #4: Hover color dark me SOLID kar diya
                      // Pehle: alpha(t.palette.primary.main, 0.1) → translucent
                      // Light mode SAME rakha: #e3f2fd
                      backgroundColor: isDark
                        ? "#2c3242 !important"
                        : "#e3f2fd !important",
                      "& td": {
                        backgroundColor: "inherit !important",
                      },
                    },
                    "& td": {
                      fontSize: "0.75rem",
                      padding: "0px 13px",
                      color: "text.primary",
                    },
                  }}
                >
                  <TableCell
                    sx={{
                      position: "sticky",
                      left: 0,
                      // ✅ Ye "inherit" Sahi hai — row (tr) ab SOLID hai (FIX #2),
                      // to inherit bhi solid hi milega. Ise CHHEDNA nahi tha.
                      bgcolor: "inherit",
                      zIndex: 2,
                    }}
                  >
                    {index + 1}
                  </TableCell>
                  <TableCell>{row.client_name}</TableCell>
                  <TableCell>{row.client_email}</TableCell>
                  <TableCell>{row.company_name}</TableCell>
                  <TableCell>{row.feedback}</TableCell>
                  <TableCell>
                    <Chip
                      label={
                        ratingOptions.find((opt) => opt.value === row.rating)
                          ?.label || "-"
                      }
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: "0.65rem",
                        bgcolor: isDark
                          ? alpha(getRatingColor(row.rating), 0.25)
                          : getRatingColor(row.rating),
                        color: isDark ? getRatingColor(row.rating) : "#fff",
                        fontWeight: 600,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={row.is_resolved ? "Yes" : "No"}
                      size="small"
                      color={row.is_resolved ? "success" : "default"}
                      sx={{ height: 20, fontSize: "0.65rem" }}
                    />
                  </TableCell>
                  <TableCell
                    sx={{
                      position: "sticky",
                      right: 0,
                      // ✅ Ye "inherit" sahi hai — row (tr) ab SOLID hai (FIX #2)
                      bgcolor: "inherit",
                      zIndex: 2,
                    }}
                  >
                    <IconButton
                      sx={{ padding: 0, paddingLeft: 1, paddingRight: 1 }}
                      onClick={() => handleEdit(row.feedback_id)}
                      color="primary"
                      size="small"
                    >
                      <EditIcon transform="scale(0.6)" />
                    </IconButton>
                    <IconButton
                      sx={{ padding: 0, paddingLeft: 1, paddingRight: 1 }}
                      color="error"
                      size="small"
                      onClick={(e) => {
                        setAnchorEl(e.currentTarget);
                        setProjectToDelete(row.feedback_id);
                      }}
                    >
                      <DeleteIcon transform="scale(0.6)" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ── ADD FEEDBACK DIALOG ── */}
      <Dialog
        open={openAddModal}
        onClose={() => setOpenAddModal(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: dialogPaperSx }}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleAddNewRow();
          }}
        >
          <DialogTitle
            sx={{
              borderBottom: "1px solid",
              borderColor: "divider",
              py: 1.5,
              color: "text.primary",
              fontWeight: 700,
              fontSize: "0.95rem",
              bgcolor: (t) =>
                t.palette.mode === "dark"
                  ? alpha(t.palette.primary.main, 0.15)
                  : alpha(t.palette.primary.main, 0.06),
            }}
          >
            Add Client Feedback
          </DialogTitle>

          <DialogContent dividers sx={{ bgcolor: "background.paper" }}>
            <TextField
              label="Client Name"
              fullWidth
              required
              margin="dense"
              value={formData.client_name}
              onChange={(e) =>
                setFormData({ ...formData, client_name: e.target.value })
              }
            />
            <TextField
              label="Client Email"
              fullWidth
              required
              type="email"
              margin="dense"
              value={formData.client_email}
              onChange={(e) =>
                setFormData({ ...formData, client_email: e.target.value })
              }
            />
            <TextField
              label="Company Name"
              fullWidth
              required
              margin="dense"
              value={formData.company_name}
              onChange={(e) =>
                setFormData({ ...formData, company_name: e.target.value })
              }
            />
            <TextField
              label="Feedback"
              fullWidth
              required
              multiline
              rows={3}
              margin="dense"
              value={formData.feedback}
              onChange={(e) =>
                setFormData({ ...formData, feedback: e.target.value })
              }
            />
            <Select
              fullWidth
              value={formData.rating}
              onChange={(e) =>
                setFormData({ ...formData, rating: e.target.value })
              }
              sx={{ mt: 1 }}
            >
              {ratingOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </DialogContent>

          <DialogActions
            sx={{
              borderTop: "1px solid",
              borderColor: "divider",
              px: 2,
              py: 1,
              bgcolor: "background.paper",
            }}
          >
            <Button
              onClick={() => setOpenAddModal(false)}
              color="error"
              variant="outlined"
              size="small"
            >
              Close
            </Button>
            <Button
              type="submit"
              color="primary"
              variant="contained"
              size="small"
            >
              Add
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* ── EDIT FEEDBACK DIALOG ── */}
      <Dialog
        open={openEditModal}
        onClose={() => setOpenEditModal(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: dialogPaperSx }}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleUpdateRow();
          }}
        >
          <DialogTitle
            sx={{
              borderBottom: "1px solid",
              borderColor: "divider",
              py: 1.5,
              color: "text.primary",
              fontWeight: 700,
              fontSize: "0.95rem",
              bgcolor: (t) =>
                t.palette.mode === "dark"
                  ? alpha(t.palette.primary.main, 0.15)
                  : alpha(t.palette.primary.main, 0.06),
            }}
          >
            Edit Feedback
          </DialogTitle>

          <DialogContent dividers sx={{ bgcolor: "background.paper" }}>
            <TextField
              label="Client Name"
              fullWidth
              required
              margin="dense"
              value={formData.client_name}
              onChange={(e) =>
                setFormData({ ...formData, client_name: e.target.value })
              }
            />
            <TextField
              label="Client Email"
              fullWidth
              required
              type="email"
              margin="dense"
              value={formData.client_email}
              onChange={(e) =>
                setFormData({ ...formData, client_email: e.target.value })
              }
            />
            <TextField
              label="Company Name"
              fullWidth
              required
              margin="dense"
              value={formData.company_name}
              onChange={(e) =>
                setFormData({ ...formData, company_name: e.target.value })
              }
            />
            <TextField
              label="Feedback"
              fullWidth
              required
              multiline
              rows={3}
              margin="dense"
              value={formData.feedback}
              onChange={(e) =>
                setFormData({ ...formData, feedback: e.target.value })
              }
            />
            <Select
              fullWidth
              value={formData.rating}
              onChange={(e) =>
                setFormData({ ...formData, rating: e.target.value })
              }
              sx={{ mt: 1 }}
            >
              {ratingOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.is_resolved}
                  onChange={(e) =>
                    setFormData({ ...formData, is_resolved: e.target.checked })
                  }
                />
              }
              label="Resolved"
              sx={{ mt: 1, color: "text.primary" }}
            />
          </DialogContent>

          <DialogActions
            sx={{
              borderTop: "1px solid",
              borderColor: "divider",
              px: 2,
              py: 1,
              bgcolor: "background.paper",
            }}
          >
            <Button
              onClick={() => setOpenEditModal(false)}
              color="error"
              variant="outlined"
              size="small"
            >
              Close
            </Button>
            <Button
              type="submit"
              color="primary"
              variant="contained"
              size="small"
            >
              Update
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* ── DELETE CONFIRMATION POPOVER ── */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        PaperProps={{
          sx: {
            bgcolor: "background.paper",
            backgroundImage: "none",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: isDark
              ? "0 8px 28px rgba(0,0,0,0.6)"
              : "0 4px 16px rgba(0,0,0,0.14)",
          },
        }}
      >
        <Box sx={{ p: 2, minWidth: 180 }}>
          <Typography
            variant="body2"
            sx={{ color: "text.primary", mb: 1 }}
          >
            Are you sure you want to delete?
          </Typography>
          <Box
            sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 1 }}
          >
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setAnchorEl(null);
                setProjectToDelete(null);
              }}
            >
              No
            </Button>
            <Button
              color="error"
              variant="contained"
              size="small"
              onClick={async () => {
                await handleDelete(projectToDelete);
                setAnchorEl(null);
                setProjectToDelete(null);
              }}
            >
              Yes
            </Button>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};

export default RoleManagementTable;
